/**
 * Editorial content for /solutions/enterprise — transcribed from the design
 * handoff prototype (`design_handoff_enterprise_page/Enterprise v1.dc.html`,
 * `renderVals()`). All of it is hardcoded marketing copy, not live data.
 */

// Owner-confirmed Enterprise terms (2026-09-27): 99.99% uptime SLA, SSO /
// SAML on request, a dedicated onboarding engineer who migrates your links.
// No hosting region is claimed; SOC 2 is not held.
export const PROOF_CHIPS = [
  "99.99% uptime SLA",
  "EU-first — visitor IPs anonymised before storage",
  "SSO / SAML on request · RBAC",
  "Dedicated onboarding engineer",
];

/** The two-lane split-scan band under the hero — one lane per reader. */
export const LANES = [
  {
    eyebrow: "Building the case?",
    accent: "text-brand-600",
    title: "Control & scale",
    body: "SSO / SAML on request, roles per workspace and revocable agent access — enough to convince your own security team before ours joins a call.",
    links: [
      { label: "Governance ↓", href: "#governance" },
      { label: "Reliability ↓", href: "#reliability" },
    ],
  },
  {
    eyebrow: "Reviewing for procurement?",
    accent: "text-data-blue",
    title: "Compliance & terms",
    body: "The artifacts you're scanning for, on one page — the 99.99% SLA, DPA, EU-first data handling, support in writing.",
    links: [
      { label: "SLA & support ↓", href: "#sla" },
      { label: "Compliance ↓", href: "#compliance" },
    ],
  },
] as const;

/** Role pill palette: fg = color, bg = color at 9% on white, border = 30%. */
export const ROLE_COLORS: Record<string, string> = {
  Owner: "var(--color-brand-600)",
  Admin: "var(--color-data-violet)",
  Editor: "var(--color-data-blue)",
  Viewer: "var(--color-data-green)",
};

export const WORKSPACES = [
  {
    name: "Marketing",
    count: "12 members",
    members: [
      { who: "lena@acme.com", role: "Owner" },
      { who: "sarah@acme.com", role: "Editor" },
    ],
  },
  {
    name: "Product",
    count: "22 members",
    members: [
      { who: "jonas@acme.com", role: "Admin" },
      { who: "max@acme.com", role: "Editor" },
    ],
  },
  {
    name: "Agencies · external",
    count: "8 members",
    members: [{ who: "partner@studio.co", role: "Viewer" }],
  },
] as const;

export const RELIABILITY_CHECKS = [
  "99.99% uptime SLA in every Enterprise agreement — the terms are spelled out there",
  "Public status page — the routing service is probed every 60 s, p50 and p99 shown live",
  "Incident history, plus email or RSS alerts — subscribe your ops channel",
];

/** Components the public status page monitors (mirrors components/status/data.ts). */
export const MONITORED = [
  { name: "Routing service", note: "p50 / p99" },
  { name: "API", note: "uptime" },
  { name: "Dashboard", note: "uptime" },
  { name: "MCP server", note: "uptime" },
  { name: "Email delivery", note: "uptime" },
];

export const SLA_ROWS = [
  { k: "Uptime target", v: "99.99% on Enterprise" },
  { k: "Coverage", v: "Every Enterprise agreement" },
  { k: "Exclusions", v: "Spelled out in your agreement, not buried" },
  { k: "Evidence", v: "Public status page with incident history" },
];

export const SUPPORT_ROWS = [
  { k: "Onboarding", v: "Dedicated engineer — we migrate your links for you" },
  { k: "Channel", v: "Dedicated Slack channel with our engineers" },
  { k: "Response times", v: "Set in your Enterprise agreement" },
  { k: "Single sign-on", v: "SSO / SAML on request, set up with our team" },
];

/** Compliance status pill palette, keyed by kind. */
export const COMPLIANCE_COLORS: Record<string, string> = {
  ok: "var(--color-data-green)",
  via: "var(--color-data-blue)",
  wip: "var(--color-data-amber)",
};

// The SOC 2 row mirrors /security: not certified, no date (owner, 2026-09-27).
export const COMPLIANCE = [
  {
    name: "EU-first data handling",
    desc: "EU-first — visitor IPs anonymised before storage, bots filtered out of analytics.",
    badge: "In force",
    kind: "ok",
    highlight: true,
  },
  {
    name: "GDPR / DSGVO",
    desc: "Our processor role and sub-processor list are in the privacy policy; the DPA covers the rest.",
    badge: "In force",
    kind: "ok",
  },
  { name: "DPA", desc: "Standard template, countersigned in days.", badge: "Via sales", kind: "via" },
  {
    name: "Security review",
    desc: "Send the questionnaire — engineers answer it, not autoresponders.",
    badge: "Via sales",
    kind: "via",
  },
  {
    name: "SOC 2",
    desc: "We hold no SOC 2 attestation today — ask for our security documentation instead.",
    badge: "Not certified",
    kind: "wip",
  },
];

export const STATS = [
  { v: "99.99%", l: "uptime SLA, in the agreement" },
  { v: "4", l: "roles, set per workspace" },
  { v: "60 s", l: "public status probe interval" },
  { v: "/24", l: "IPv4 truncation before storage" },
];
